import { STANDIN_CURRENCIES, allCoinParts, coinCount, coinParts, type Coin, type CoinPart, type StandinCurrency } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The wallet line's words (P2.8-T38, P2.8-T59 and P2.8-T67, ADR-124 decision 5.5 and its notes for P2.8-T59 and P2.8-T67): each coin's name, one and many, drafts for the author's
 * language files with the page's other words. The players' names are the author's (ADR-124's answer 1: "Sovereign,
 * Talon, Bit").
 */
export const COIN_NAMES: { readonly [coin in Coin]: { readonly one: string; readonly many: string } } = {
  gold: { one: say("wallet.coins.goldOne"), many: say("wallet.coins.goldMany") },
  silver: { one: say("wallet.coins.silverOne"), many: say("wallet.coins.silverMany") },
  copper: { one: say("wallet.coins.copperOne"), many: say("wallet.coins.copperMany") },
};

/**
 * Each coin's colour where its art is not drawn, the `coin` glyph's (ADR-124 decision 5.3): the design's gold
 * (`--ot-stamina`), `--ot-tier-silver` and `--ot-tier-bronze`.
 */
export const COIN_COLOURS: { readonly [coin in Coin]: string } = {
  gold: "var(--ot-stamina)",
  silver: "var(--ot-tier-silver)",
  copper: "var(--ot-tier-bronze)",
};

/**
 * Words joined as the page's language lists them: "a", "a and b", "a, b and c".
 *
 * @param words The words, at least one.
 * @returns The list.
 */
function listed(words: readonly string[]): string {
  if (words.length < 2) {
    return words[0] ?? "";
  }
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1] as string}`;
}

/**
 * An amount of Valish coin in the full form, for the wallet line's title and accessible name (ADR-124 decision 5.5):
 * each part as its number and its coin's name, one or many, the Sovereign part grouped, joined as a list.
 *
 * @param copper The amount in copper.
 * @returns The full form.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 * @example
 * fullCoins(34512);
 * // "3 Sovereigns, 45 Talons and 12 Bits"
 */
export function fullCoins(copper: number): string {
  return listed(coinParts(copper).map(coinPartName));
}

/**
 * One coin part in the full form: its number, the Sovereign part grouped, and its coin's name, one for exactly 1 and
 * many otherwise, 0 included. A wallet line's coin part is named and shown by it (ADR-124's note for P2.8-T67).
 *
 * @param part The part.
 * @returns The part's words.
 * @example
 * coinPartName({ coin: "silver", count: 1 });
 * // "1 Talon"
 * coinPartName({ coin: "gold", count: 0 });
 * // "0 Sovereigns"
 */
export function coinPartName(part: CoinPart): string {
  return `${coinCount(part)} ${part.count === 1 ? COIN_NAMES[part.coin].one : COIN_NAMES[part.coin].many}`;
}

/**
 * A gain of copper in full words, as the coin toast titles it (ADR-124's note for the coin toast's words): a plus sign,
 * then each part the amount holds, largest first, named and split by commas; a part of 0 is left out.
 *
 * @param copper The gain in copper, above 0.
 * @returns The words.
 * @example
 * coinGainWords(34_512);
 * // "+3 Sovereigns, 45 Talons, 12 Bits"
 * coinGainWords(101);
 * // "+1 Talon, 1 Bit"
 */
export function coinGainWords(copper: number): string {
  return `+${coinParts(copper).map(coinPartName).join(", ")}`;
}

/**
 * Each stand-in currency's name, Bible 10.1's and ADR-124's (ADR-124's note for P2.8-T59).
 */
export const STANDIN_NAMES: { readonly [code in StandinCurrency]: string } = {
  honor: say("wallet.standin.honor"),
  marks: say("wallet.standin.marks"),
  tokens: say("wallet.standin.tokens"),
};

/**
 * The name and tooltip of a stand-in currency in the wallet line (ADR-124's notes for P2.8-T59 and P2.8-T67), a draft:
 * it reads 0 because no system earns it yet.
 *
 * @param code The currency.
 * @returns The name.
 * @example
 * standinName("honor");
 * // "Honor: not earned yet"
 */
export function standinName(code: StandinCurrency): string {
  return say("wallet.standinNotEarned", { name: STANDIN_NAMES[code] });
}

/**
 * The accessible name of the wallet line's group of coin parts (ADR-124's note for P2.8-T67), a draft: the currency's
 * name, so a screen reader announces the group before each part names itself.
 */
export const COINS_GROUP_NAME = say("wallet.coinsGroup");

/**
 * One mark of the wallet line: a coin part by its coin, or a stand-in currency by its code.
 */
export type WalletMark = { readonly kind: "coin"; readonly coin: Coin } | { readonly kind: "currency"; readonly code: StandinCurrency };

/**
 * The wallet line's marks in the order drawn, each mark's place in it its tooltip anchor's index (`wallet:<n>`, ADR-124's
 * note for P2.8-T67): the Sovereign, the Talon and the Bit, then Honor, Marks of the Lyceum and Inscription Tokens.
 */
export const WALLET_MARKS: readonly WalletMark[] = Object.freeze([
  ...allCoinParts(0).map((part): WalletMark => ({ kind: "coin", coin: part.coin })),
  ...STANDIN_CURRENCIES.map((code): WalletMark => ({ kind: "currency", code })),
]);

/**
 * The anchor a wallet mark's tooltip hangs from, as its `data-tip` attribute.
 *
 * @param index The mark's place in `WALLET_MARKS`.
 * @returns `wallet:<index>`.
 * @example
 * walletTipAnchor(3);
 * // "wallet:3"
 */
export function walletTipAnchor(index: number): string {
  return `wallet:${index}`;
}

/**
 * What a wallet mark's tooltip and accessible name read (ADR-124's note for P2.8-T67): a coin part its number and its
 * coin's name, one or many; a stand-in its name and "not earned yet".
 *
 * @param copper The purse in copper.
 * @param index The mark's place in `WALLET_MARKS`.
 * @returns The words, or null for no such mark.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 * @example
 * walletMarkName(30012, 1);
 * // "0 Talons"
 */
export function walletMarkName(copper: number, index: number): string | null {
  const mark = WALLET_MARKS[index];
  if (mark === undefined) {
    return null;
  }
  if (mark.kind === "currency") {
    return standinName(mark.code);
  }
  const part = allCoinParts(copper).find((each) => each.coin === mark.coin);
  return part === undefined ? null : coinPartName(part);
}
